/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleComponentsAddDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 04-03-2025
 * @description : Component to add vehicle components.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import EnterSelectTextField from "../text-fields/EnterSelectTextField";
import {
  HandleApiActions,
  adminSettingsAPI,
  labCar,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import MultiselectConnectorV2 from "../customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomCheckBoxV2 from "../customComponentsV2/common/checkBox/CustomCheckBoxV2";
import CustomSelectV2 from "../customComponentsV2/common/select/CustomSelectV2";
import UsersMultiselect from "./UsersMultiselect";
import CustomTabsV2 from "../customComponentsV2/common/tabs/CustomTabsV2";
import AttributesForm from "../../pages/labcar/Attributes/AttributesForm";

function VehicleComponentsAddDialog({
  openDialog,
  setOpenDialog,
  title,
  variantOptionList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  supplierOptionsList,
  vcOptionsList = [],
  getVehicleComponents,
  recreationMode,
  recreationParentVCList,
  selectedParentVCDetails,
  setSelectedParentVCDetails,
}) {
  /**
   * ===================== State Initialization =====================
   */
  /** ============== Redux ============== **/
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );
  const selectedVehicleSystemId = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemId,
  );
  const selectedVehicleSystemDetails = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemDetails,
  );
  /** ============== Add Dialog ============== **/
  const [selectedVehicleComponent, setSelectedVehicleComponent] =
    useState(null);
  const [componentNo, setComponentNo] = useState("");
  const [componentNoError, setComponentNoError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [validateShowAddInSettings, setValidateShowAddInSettings] =
    useState(false);
  const [addInSettings, setAddInSettings] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [vsVariantOptionList, setVsVariantOptionList] = useState([]);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [vcAddTabValue, setVcAddTabValue] = useState(0);
  // recreate state
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);

  const normalizeSupplierOptions = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") return { value: opt, label: opt };
        const value = opt.value ?? opt.id ?? opt.name ?? opt.label;
        const label = opt.label ?? opt.name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        return { value, label };
      })
      .filter(Boolean);
  };

  const suppliersFromProps = useMemo(
    () => normalizeSupplierOptions(supplierOptionsList),
    [supplierOptionsList],
  );

  const suppliersFromStore = useMemo(() => {
    const list = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return normalizeSupplierOptions(list);
  }, [adminSettingsDetails]);

  const resolvedSupplierOptionsList =
    suppliersFromProps.length > 0 ? suppliersFromProps : suppliersFromStore;

  // Fetch admin settings when dialog opens if suppliers are not available yet.
  useEffect(() => {
    if (!openDialog) return;
    if (resolvedSupplierOptionsList.length > 0) return;

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_admin_settings,
        show_toast: false,
      }),
    );
    setVcAddTabValue(0);
  }, [openDialog, resolvedSupplierOptionsList.length, dispatch]);

  /**
   * ===================== Effect Hooks =====================
   */
  // Resets the addInSettings
  // Dependency: selectedVehicleComponent
  useEffect(() => {
    setAddInSettings(false);
  }, [selectedVehicleComponent]);
  // Resets reason, and reason error when the selected vehicle component changes.
  // Dependency: selectedVehicleComponent,
  useEffect(() => {
    setReason("");
    setReasonError("");
  }, [selectedVehicleComponent, addInSettings]);
  // Updates validateShowAddInSettings based on selectedVehicleComponent
  // Dependencies: vcOptionsList, selectedVehicleComponent
  useEffect(() => {
    const filteredVcOptionsList =
      vcOptionsList?.map((vcOL) => vcOL?.label?.toUpperCase()) ?? [];
    const showAddInSettings = selectedVehicleComponent
      ? filteredVcOptionsList.includes(selectedVehicleComponent?.toUpperCase())
      : false;
    setValidateShowAddInSettings(showAddInSettings || false);
  }, [vcOptionsList, selectedVehicleComponent]);
  // For the vehicle component only show the variants in which the vehicle system is present
  // and always include the active variant in the options list
  useEffect(() => {
    let filteredVariantOptionList =
      variantOptionList?.filter((option) =>
        selectedVehicleSystemDetails?.variant_ids?.includes(
          Number(option?.value),
        ),
      ) || [];
    // Ensure the active variant is always in the options list
    if (selectedSingleVariantDetails?.variant_id != null) {
      const activeId = selectedSingleVariantDetails.variant_id;
      const hasActive = filteredVariantOptionList.some(
        (option) => String(option.value) === String(activeId),
      );
      if (!hasActive) {
        const found = variantOptionList?.find(
          (option) => String(option.value) === String(activeId),
        );
        if (found) {
          filteredVariantOptionList = [found, ...filteredVariantOptionList];
        }
      }
    }
    setVsVariantOptionList(filteredVariantOptionList);
  }, [
    variantOptionList,
    selectedVehicleSystemDetails,
    selectedSingleVariantDetails,
  ]);
  // By default select the variants in which the vehicle system is present
  // and pre-select active variant when dialog opens, similar to Add Software Modules logic
  useEffect(() => {
    let baseVariants = selectedVehicleSystemDetails?.variant_ids || [];
    if (selectedSingleVariantDetails?.variant_id != null) {
      const id = selectedSingleVariantDetails.variant_id;
      const hasId = baseVariants.map(String).includes(String(id));
      if (!hasId) {
        baseVariants = [id, ...baseVariants];
      }
    }
    setSelectedVariants(baseVariants);
  }, [
    openDialog,
    vsVariantOptionList,
    selectedVehicleSystemDetails,
    selectedSingleVariantDetails,
  ]);

  /**
   * ===================== API Call Functions =====================
   */
  const handleVCAttributeAdd = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const addedAttributeFormRows = useMemo(
    () =>
      (Array.isArray(attributeFormData?.added)
        ? attributeFormData.added
        : []
      ).map((attribute) => ({
        ...attribute,
        attribute_id: null,
        newlyAdded: true,
        edited: false,
      })),
    [attributeFormData?.added],
  );
  /**
   * @author : Narender
   * @Date : 03-03-2025
   * @description : Adds a vehicle component
   * @param {project_id, variant_id, vs_id, name, component_number, description, suppliers}
   * @return {msg: "", data: [], error: true | false}
   */
  const addVehicleComponent = async () => {
    const apiDetails = labCar.add_vehicle_component;
    handleCloseDialog();

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: true,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          vs_id: Number(selectedVehicleSystemId),
          name: selectedVehicleComponent,
          component_number: componentNo?.trim(),
          description: description,
          suppliers: selectedSuppliers,
          ...(selectedVariants?.length
            ? { variant_ids: selectedVariants }
            : {}),
          ...(addInSettings
            ? { add_vc_admin_vs: true, add_vc_reason: reason }
            : {}),
          attributes: attributeFormData.added,
        },
      }),
    );
    if (!resp.error) {
      getVehicleComponents();
    }
  };

  // recreate function
  const setFormStates = async (params) => {
    setComponentNo(params?.component_number);
    setSelectedVehicleComponent(params?.name);
    setDescription(params?.description);
    setSelectedSuppliers(params?.suppliers);
  };
  const handleRecreationAddClick = async () => {
    const apiDetails = labCar.recreate_vc;
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      vs_id: Number(selectedVehicleSystemId),
      name: selectedVehicleComponent,
      component_number: componentNo?.trim(),
      description: description,
      suppliers: selectedSuppliers,
      ...(selectedVariants?.length ? { variant_ids: selectedVariants } : {}),
      ...(addInSettings
        ? { add_vc_admin_vs: true, add_vc_reason: reason }
        : {}),
      co_owners: coOwners?.map((data) => data?.value),
      edit_users: editors?.map((data) => data?.value),
      view_users: viewers?.map((data) => data?.value),
      parent_project_id: selectedProjectDetails?.parent_project_id,
      parent_vc_id: selectedParentVCDetails?.id,
    };
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: true,
        params: reqObj,
      }),
    );

    if (!resp.error) {
      getVehicleComponents();
      handleCloseDialog();
    }
  };

  useEffect(() => {
    if (selectedParentVCDetails) {
      setFormStates(selectedParentVCDetails);
    }
  }, [selectedParentVCDetails]);

  /**
   * ===================== UI & State Management Functions =====================
   */
  // Validates the fields in the dialog
  const isDialogFieldsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      componentNo?.trim() !== "" &&
      componentNo?.trim()?.length >= 3 &&
      !componentNoError &&
      regexMin3.test(componentNo.trim()) &&
      selectedVehicleComponent?.trim()?.length > 0 &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      !descriptionError &&
      selectedSuppliers?.length > 0 &&
      (addInSettings ? reason?.trim() !== "" && !reasonError : true)
    );
  };
  // Clears all input fields in the dialog
  const clearDialogFields = () => {
    setComponentNo("");
    setComponentNoError("");
    setSelectedVehicleComponent(null);
    setDescription("");
    setDescriptionError("");
    setSelectedSuppliers([]);
    setAddInSettings(false);
    setReason("");
    setReasonError("");
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };
  // Handles closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    clearDialogFields();
  };

  return (
    <CustomDialogBoxV2
      open={openDialog}
      onClose={handleCloseDialog}
      title={title}
      dialogActions={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            {vcAddTabValue === 1 && !recreationMode && (
              <CustomButtonV2
                type="cancel"
                size="small"
                btnName={"Back"}
                handleOnClick={() => {
                  setVcAddTabValue(0);
                }}
              />
            )}
          </Box>
          <CustomButtonV2
            type="submit"
            size="small"
            disabled={!isDialogFieldsValid()}
            btnName={vcAddTabValue === 0 ? "Next" : "Add"}
            handleOnClick={() => {
              if (recreationMode) {
                handleRecreationAddClick();
              } else {
                if (vcAddTabValue === 0) {
                  setVcAddTabValue(1);
                } else {
                  addVehicleComponent();
                }
              }
            }}
          />
        </Box>
      }
    >
      <CustomTabsV2
        defaultTab={vcAddTabValue}
        value={vcAddTabValue}
        onTabChange={(newValue) => setVcAddTabValue(newValue)}
        tabs={[
          {
            name: "Details",
            content: (
              <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                {recreationMode && (
                  <CustomSelectV2
                    size={"small"}
                    label={"Select Parent VC"}
                    required={true}
                    optionsList={recreationParentVCList?.map((item) => {
                      return {
                        label: item?.component_number,
                        value: item,
                      };
                    })}
                    selectedValue={selectedParentVCDetails || " "}
                    handleOnChange={(e) =>
                      setSelectedParentVCDetails(e.target.value)
                    }
                  />
                )}
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Component No"}
                  required={true}
                  type={"text"}
                  fldType={"textR"}
                  value={componentNo}
                  setValue={(value) =>
                    value.length <= 40 && setComponentNo(value)
                  }
                  maxLength={40}
                />

                <EnterSelectTextField
                  size={"small"}
                  label={"Select / Enter Vehicle Component"}
                  required={true}
                  optionsList={vcOptionsList}
                  value={selectedVehicleComponent}
                  setValue={setSelectedVehicleComponent}
                  height={{ height: "32px" }}
                />
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Description"}
                  required={true}
                  value={description}
                  setValue={(value) =>
                    value.length <= 500 && setDescription(value)
                  }
                  maxLength={500}
                />
                <MultiselectConnectorV2
                  required
                  mutiple
                  size={"small"}
                  label={"Select Suppliers"}
                  optionsList={resolvedSupplierOptionsList?.map((item) => ({
                    value: item?.value.toUpperCase(),
                    label: item?.label,
                  }))}
                  selectedValues={selectedSuppliers || " "}
                  setSelectedValues={setSelectedSuppliers}
                  placeholder={false}
                />
                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedVariants || " "}
                  setSelectedValues={setSelectedVariants}
                  label={"Select Variants"}
                  optionsList={vsVariantOptionList}
                  placeholder={false}
                  immutableValues={
                    selectedSingleVariantDetails?.variant_id != null
                      ? [selectedSingleVariantDetails?.variant_id]
                      : []
                  }
                  disabled={true}
                />
                {recreationMode && (
                  <UsersMultiselect
                    coOwnerDetails={
                      selectedParentVCDetails?.co_owner_details || []
                    }
                    editorDetails={
                      selectedParentVCDetails?.edit_user_details || []
                    }
                    viewerDetails={
                      selectedParentVCDetails?.view_user_details || []
                    }
                    coOwners={coOwners}
                    setCoOwners={setCoOwners}
                    editors={editors}
                    setEditors={setEditors}
                    viewers={viewers}
                    setViewers={setViewers}
                    owner={[selectedParentVCDetails?.created_by]}
                  />
                )}

                {/*  Check if a `selectedVehicleComponent` exists and is NOT in `vcOptionsList` */}
                {selectedVehicleComponent && !validateShowAddInSettings ? (
                  <Box display="flex" alignItems="center">
                    <CustomCheckBoxV2
                      checked={addInSettings}
                      onChange={() => {
                        setAddInSettings((prev) => !prev);
                        setReasonError("");
                      }}
                      sx={{ pl: 0 }}
                    />
                    <Typography sx={{ cursor: "default" }}>
                      Request to add in settings
                    </Typography>
                  </Box>
                ) : null}
                {addInSettings &&
                  selectedVehicleComponent &&
                  !validateShowAddInSettings ? (
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Reason"}
                    required={true}
                    value={reason}
                    setValue={(value) =>
                      value.length <= 100 && setReason(value)
                    }
                  />
                ) : null}
              </Stack>
            ),
          },
          {
            name: "Attribute",
            content: (
              <Stack
                spacing={2}
                mb={2}
                justifyContent={"center"}
                sx={{ width: "450px" }}
              >
                <AttributesForm
                  type={"ADD"}
                  attributeFromApi={addedAttributeFormRows}
                  getFormData={handleVCAttributeAdd}
                />
              </Stack>
            ),
          },
        ]}
      />
    </CustomDialogBoxV2>
  );
}

export default VehicleComponentsAddDialog;
